import { queryOptions, useQuery, type UseQueryResult } from '@tanstack/react-query'

import type { MarketRange } from '@/features/market/api/marketApi'
import { priceHistoryQueryOptions } from '@/features/prices/api/queries'
import type { PriceHistory, PriceHistoryPoint } from '@/schemas/prices'

/*
 * The stock's recent sessions — kitta and rupees traded each day — for the
 * two figures Technical and Advanced › Trading average over thirty sessions:
 * the "normal day" today's volume is measured against, and Trading's
 * liquidity. Both tabs read this ONE query, so the Volume chip on Trading and
 * the volume card on Technical are worked out from the same days.
 *
 * It is the Price history read, not a second one: the same endpoint, through
 * the same mapper (`mapPriceHistory`) and under the same key as that tab's own
 * three-month range, so nothing here can drift from what its table prints.
 * `usePriceHistory` itself is not called because it keeps the previous key's
 * data on screen while the next one loads — right for a range chip, wrong for
 * an average that would then belong to another company.
 */

/**
 * Three months — the shortest range that always holds thirty sessions. `1M`
 * is a calendar month (23 sessions on the seed); `3M` is 91 days, about 65
 * NEPSE sessions, which still clears thirty across Dashain and Tihar.
 */
export const SESSIONS_RANGE: MarketRange = '3M'

/** A day's own figures, as the price history carries them. */
export type Session = PriceHistoryPoint

/**
 * Five minutes: one row a session, so the average cannot move inside one —
 * the window `/technicals/` gets for the same reason.
 */
const SESSIONS_STALE_MS = 5 * 60_000

export const recentSessionsQueryOptions = (symbol: string) =>
  queryOptions({
    ...priceHistoryQueryOptions(symbol, SESSIONS_RANGE),
    staleTime: SESSIONS_STALE_MS,
  })

/** Oldest first, as the endpoint serves them. */
const sessionsOf = (history: PriceHistory): Session[] => history.points

export const useRecentSessions = (symbol: string): UseQueryResult<Session[]> =>
  useQuery({ ...recentSessionsQueryOptions(symbol), select: sessionsOf })
